import { z } from 'zod';
import { TMDXFile } from '@/api/apis/files';
import { getPhoneSchema } from '@/utils/helpers';
import { ContactFormTypeUpdated } from '@/types';
import { addCustomIssue, validateInviteAsUserFields } from '../shared/invite-validation';

export const CONTACT_FORM_DEFAULT_VALUES: ContactFormTypeUpdated = {
  firstName: '',
  lastName: '',
  email: '',
  primaryPhone: '',
  secondaryPhone: '',
  photoPath: '',
};

export const contactSchema = z.object({
  firstName: z
    .string()
    .min(1, 'Provide first name')
    .refine((value) => value.trim().length > 0, { message: 'First name cannot be empty or whitespace' }),
  lastName: z
    .string()
    .min(1, 'Provide last name')
    .refine((value) => value.trim().length > 0, { message: 'Last name cannot be empty or whitespace' }),
  photo: z.custom<TMDXFile>().optional(),
  photoPath: z.string().optional(),
  email: z.union([z.literal(''), z.string().email({ message: 'Invalid email' })]),
  primaryPhone: getPhoneSchema({ isRequired: true }),
  secondaryPhone: getPhoneSchema({ isRequired: false }),
});

export const contactSchemaOptional = {
  firstName: z.union([z.string(), z.literal(''), z.undefined()]).optional(),
  lastName: z.union([z.string(), z.literal(''), z.undefined()]).optional(),
  photo: z.custom<TMDXFile>().optional(),
  photoPath: z.string().optional(),
  email: z.union([z.literal(''), z.string().email({ message: 'Invalid email' }), z.null(), z.undefined()]).optional(),
  primaryPhone: getPhoneSchema({ isRequired: false }),
  secondaryPhone: getPhoneSchema({ isRequired: false }),
};

export const opoContactSchema = contactSchema
  .extend({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.union([z.literal(''), z.string().email({ message: 'Invalid email' })]).optional(),
    photoPath: z.string().nullish().optional(),

    inviteAsUser: z.boolean().optional(),
    memberships: z
      .array(
        z.object({
          opoOrganizationId: z.string().min(1, 'OPO organization is required'),
          roles: z.array(z.string()),
        }),
      )
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.inviteAsUser) {
      return;
    }

    validateInviteAsUserFields(data, ctx);

    const roles = data.memberships?.[0]?.roles ?? [];

    if (!roles.length) {
      addCustomIssue(ctx, ['memberships', 0, 'roles'], 'Provide at least one role');
    }
  });
